import { useEffect, useRef, useState } from 'react'
import { Calculator, Delete } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MENU_SURFACE } from '@/components/ui/menu-surface'
import {
  INITIAL_CALCULATOR_STATE,
  displayValue,
  pressClear,
  pressBackspace,
  pressDecimal,
  pressDigit,
  pressEquals,
  pressOperator,
  type CalcOperator,
  type CalculatorState,
} from '@/lib/calculatorEngine'

const KEY_CLS =
  'h-auto w-full rounded-md py-2 text-sm font-medium text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-hover)]'
const OP_CLS =
  'h-auto w-full rounded-md py-2 text-sm font-medium text-[var(--fl-primary)] hover:bg-[var(--fl-primary-subtle)]'

interface AmountCalculatorInputProps {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  name?: string
  id?: string
  placeholder?: string
  className?: string
  max?: number
  'aria-label'?: string
}

/** An amount field with a built-in popup calculator (+ − × ÷), so "1200 + 450" can be
 * worked out in place instead of on a separate phone/desk calculator. Typing directly
 * into the field still works exactly as before — the calculator is purely additive. */
export function AmountCalculatorInput({
  value,
  onChange,
  onBlur,
  name,
  id,
  placeholder,
  className = '',
  max,
  'aria-label': ariaLabel,
}: AmountCalculatorInputProps) {
  const [open, setOpen] = useState(false)
  const [calc, setCalc] = useState<CalculatorState>(INITIAL_CALCULATOR_STATE)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open])

  function openCalculator() {
    // Prefill with whatever's already typed, so "add to it" works without retyping.
    const prefill = value && !Number.isNaN(parseFloat(value)) ? value : ''
    setCalc({ ...INITIAL_CALCULATOR_STATE, display: prefill })
    setOpen(true)
  }

  function applyEquals() {
    const result = pressEquals(calc)
    setCalc(result)
    if (!result.error) {
      onChange(displayValue(result))
      setOpen(false)
    }
  }

  function pressOp(op: CalcOperator) {
    setCalc(pressOperator(calc, op))
  }

  const pendingLine =
    calc.previousValue !== null && calc.pendingOp ? `${calc.previousValue} ${calc.pendingOp}` : ' ' // non-breaking space keeps the line's height reserved either way

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        type="number"
        step="0.01"
        max={max}
        name={name}
        id={id}
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={`pr-9 ${className}`}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        tooltip="Open calculator"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={openCalculator}
        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-[var(--fl-text-3)]"
      >
        <Calculator size={15} />
      </Button>

      {open && (
        <div
          role="dialog"
          aria-label="Calculator"
          className={`${MENU_SURFACE} absolute right-0 top-full z-20 mt-1 w-56 p-2`}
        >
          <div className="mb-2 rounded-md bg-[var(--fl-surface-subtle)] px-3 py-2 text-right">
            <p className="text-xs text-[var(--fl-text-3)] leading-tight">{pendingLine}</p>
            <p className="truncate text-lg font-semibold text-[var(--fl-text-1)] leading-tight">
              {displayValue(calc)}
            </p>
          </div>

          <div className="grid grid-cols-4 gap-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setCalc(pressClear())}
              className={KEY_CLS}
            >
              C
            </Button>
            <Button
              type="button"
              variant="ghost"
              aria-label="Backspace"
              onClick={() => setCalc(pressBackspace(calc))}
              className={KEY_CLS}
            >
              <Delete size={15} className="mx-auto" />
            </Button>
            <div />
            <Button type="button" variant="ghost" onClick={() => pressOp('÷')} className={OP_CLS}>
              ÷
            </Button>

            {(['7', '8', '9'] as const).map((d) => (
              <Button
                key={d}
                type="button"
                variant="ghost"
                onClick={() => setCalc(pressDigit(calc, d))}
                className={KEY_CLS}
              >
                {d}
              </Button>
            ))}
            <Button type="button" variant="ghost" onClick={() => pressOp('×')} className={OP_CLS}>
              ×
            </Button>

            {(['4', '5', '6'] as const).map((d) => (
              <Button
                key={d}
                type="button"
                variant="ghost"
                onClick={() => setCalc(pressDigit(calc, d))}
                className={KEY_CLS}
              >
                {d}
              </Button>
            ))}
            <Button type="button" variant="ghost" onClick={() => pressOp('-')} className={OP_CLS}>
              −
            </Button>

            {(['1', '2', '3'] as const).map((d) => (
              <Button
                key={d}
                type="button"
                variant="ghost"
                onClick={() => setCalc(pressDigit(calc, d))}
                className={KEY_CLS}
              >
                {d}
              </Button>
            ))}
            <Button type="button" variant="ghost" onClick={() => pressOp('+')} className={OP_CLS}>
              +
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={() => setCalc(pressDigit(calc, '0'))}
              className={`${KEY_CLS} col-span-2`}
            >
              0
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setCalc(pressDecimal(calc))}
              className={KEY_CLS}
            >
              .
            </Button>
            <Button
              type="button"
              onClick={applyEquals}
              className="h-auto w-full rounded-md py-2 text-sm font-semibold"
            >
              =
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
